import React, { ReactNode } from "react";
import client from "@/api/apollo";
import { LOG_CLIENT_ERROR } from "@/api/mutations";
import { Box } from "@chakra-ui/react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorStack?: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: "", errorStack: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message,
      errorStack: error.stack,
    };
  }

  componentDidCatch(error: any, errorInfo: any) {
    client.mutate({
      mutation: LOG_CLIENT_ERROR,
      variables: {
        message: error?.message || "Unknown error",
        stack: error?.stack || errorInfo?.componentStack,
        source: "ErrorBoundary",
      },
    });
  }

  render() {
    const { hasError, errorMessage, errorStack } = this.state;

    if (hasError) {
      return (
        <Box p={8} color="red" whiteSpace="pre-wrap">
          <h2>Something went wrong.</h2>
          <p><strong>Error:</strong> {errorMessage}</p>
          <details>
            <summary>Stack trace</summary>
            <pre>{errorStack}</pre>
          </details>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;